// @tennis-matches/services/trainerstunden/planning-trainer-column-visibility

import { authEmailsEquivalent } from "./normalize-email-for-auth-match.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

/** @param {unknown} viewer */
export function isPlanningCoordinatorViewer(viewer) {
  const mode = __asStr(viewer?.mode).toLowerCase() || "coordinator";
  return mode === "coordinator" || mode === "planner";
}

/**
 * @param {unknown[]} trainers
 * @param {{ authUserId?: string, authEmail?: string, viewerTrainerId?: string }} [opts]
 * @returns {Set<string>}
 */
export function resolveMyTrainerIds(trainers, opts = {}) {
  const ids = new Set();
  const uid = __asStr(opts.authUserId);
  const email = __asStr(opts.authEmail).toLowerCase();
  const viewerTrainerId = __asStr(opts.viewerTrainerId);
  if (viewerTrainerId) ids.add(viewerTrainerId);

  for (const tr of Array.isArray(trainers) ? trainers : []) {
    const id = __asStr(tr?.id);
    if (!id) continue;
    const createdBy = __asStr(tr?.created_by);
    if (email && authEmailsEquivalent(tr?.login_email, email)) ids.add(id);
    if (uid && createdBy && createdBy === uid) ids.add(id);
  }
  return ids;
}

/**
 * Trainer-IDs des eingeloggten Trainers (Viewer-ID + Gmail-äquivalente Login-Mails).
 * Ohne created_by, damit angelegte Kollegen-Datensätze nicht in den Scope rutschen.
 *
 * @param {unknown[]} trainers
 * @param {{ authEmail?: string, viewerTrainerId?: string }} [opts]
 * @returns {string[]}
 */
export function resolveViewerOwnTrainerIds(trainers, opts = {}) {
  const ids = [];
  const seen = new Set();
  const add = (raw) => {
    const id = __asStr(raw);
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  add(opts.viewerTrainerId);
  const email = __asStr(opts.authEmail);
  for (const tr of Array.isArray(trainers) ? trainers : []) {
    if (email && authEmailsEquivalent(tr?.login_email, email)) add(tr?.id);
  }
  return ids;
}

/** @param {unknown} viewer */
export function shouldShowPlanningTrainerField(viewer) {
  return isPlanningCoordinatorViewer(viewer);
}

/**
 * @param {unknown} viewer
 * @param {unknown[]} trainers
 * @param {{ userId?: string, email?: string }} [authIdentity]
 */
export function shouldShowPlanningTrainerColumn(
  viewer,
  trainers,
  authIdentity = {},
) {
  if (!isPlanningCoordinatorViewer(viewer)) return false;

  const list = (Array.isArray(trainers) ? trainers : []).filter((tr) =>
    __asStr(tr?.id),
  );
  if (list.length === 0) return false;

  const myIds = resolveMyTrainerIds(list, {
    authUserId: authIdentity.userId,
    authEmail: authIdentity.email,
    viewerTrainerId: viewer?.trainerId,
  });

  return list.some((tr) => !myIds.has(__asStr(tr.id)));
}

/**
 * Trainer-Auswahl im Anlage-Formular (Web: Koordinator; Native: mehrere Trainer).
 *
 * @param {{ viewer?: unknown, trainers?: unknown[] }} [opts]
 */
export function shouldShowPlanningTrainerPickerInSessionForm(opts = {}) {
  if (opts.viewer != null) {
    return shouldShowPlanningTrainerField(opts.viewer);
  }
  const list = (Array.isArray(opts.trainers) ? opts.trainers : []).filter(
    (tr) => tr && !tr.is_inactive,
  );
  return list.length > 1;
}

/**
 * @param {boolean} showTrainerPicker
 * @returns {"trainerstunden.planning.sectionTrainerAndPlayers"|"trainerstunden.planning.sectionPlayers"}
 */
export function resolvePlanningParticipantsSectionTitleKey(showTrainerPicker) {
  return showTrainerPicker
    ? "trainerstunden.planning.sectionTrainerAndPlayers"
    : "trainerstunden.planning.sectionPlayers";
}
